import { useState, useRef, useEffect } from "react";
import {
  FaHome, FaShoppingBag, FaSearch, FaHeart, FaShoppingCart
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { apiFetch } from "../api";
import ProductModal from "./ProductModal";
import AadviIcon from "../assets/Aadvi-logo.png";
import "./BottomNavbar.css";

const BottomNavbar = () => {
    // State variables
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showSearchBar, setShowSearchBar] = useState(false);
    const [isIconSpinning, setIsIconSpinning] = useState(false);
    const [isExtraNavbarOpen, setIsExtraNavbarOpen] = useState(false);
    // Refs and hooks
    const searchRef = useRef(null);
    const wishlist = useSelector((state) => state.wishlist);
    const cart = useSelector((state) => state.cart);
    const navigate = useNavigate();
    const location = useLocation();
    // Handlers
    const handleSearch = (e) => {
        // search handler
        const query = e.target.value;
        setSearchQuery(query);
        // Normalize the query for case-insensitive and space-insensitive matching
        const normalize = (str) => str.toLowerCase().replace(/\s/g, "");
        const normalizedQuery = normalize(query);
        // If query is empty, clear results
        if (normalizedQuery === "") {
            setSearchResults([]);
            setShowSearchResults(false);
            return;
        }
        // Filter products based on normalized names
        const filtered = products.filter((product) => {
            // Normalize product name
            const normalizedName = normalize(product.name);
            return normalizedName.includes(normalizedQuery);
        });
        // Set search results and show them
        setSearchResults(filtered);
        setShowSearchResults(true);
    };
    useEffect(() => {
        // Listen for bottom search open events
        const handleBottomSearch = (e) => {
            if (e.detail.open) {
                setShowSearchResults(false);
                setSearchQuery("");
            }
        };
        // Add event listener
        window.addEventListener('bottomSearchOpen', handleBottomSearch);
        return () => window.removeEventListener('bottomSearchOpen', handleBottomSearch);
    }, []);
    // Derived state
    const shouldShowNoMatches = showSearchResults && searchQuery.trim() !== "" && searchResults.length === 0;

    useEffect(() => {
        // Fetch products on location change
        const fetchProducts = async () => {
            try {
                // Fetch all products
                const data = await apiFetch("/api/products/all");
                if (data && data.data) {
                    setProducts(data.data);
                } else {
                    setProducts([]);
                }
            } catch (error) {
                setProducts([]);
            }
        };
        fetchProducts();
    }, [location]); 

    useEffect(() => {
        setShowSearchResults(false);
        setShowSearchBar(false);
        setSearchQuery("");
    }, [location]);

    useEffect(() => {
        // Handle scroll to close search and extra navbar
        const handleScroll = () => {
            setShowSearchResults(false);
            setShowSearchBar(false);
            setSearchQuery("");
        };
        // Add scroll event listener
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const toggleExtraNavbar = () => {
        // Toggle extra navbar visibility with icon spin
        setIsIconSpinning(true);
        setIsExtraNavbarOpen(!isExtraNavbarOpen);
        setTimeout(() => setIsIconSpinning(false), 500); // Spin duration
    };

    useEffect(() => {
        // Handle scroll to close search and extra navbar
        const handleScroll = () => {
            setShowSearchResults(false);
            setShowSearchBar(false);
            setSearchQuery("");
            setIsExtraNavbarOpen(false); 
        };
        // Add scroll event listener
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        // Reset states on location change
        setShowSearchResults(false);
        setShowSearchBar(false);
        setSearchQuery("");
        setIsExtraNavbarOpen(false); // Close extra navbar on location change
    }, [location]);

    useEffect(() => {
        // Handle clicks outside search and extra navbar
        const handleClickOutside = (event) => {
            if (
                // Close search results if clicked outside
                searchRef.current &&
                !searchRef.current.contains(event.target)
            ) {
                setShowSearchResults(false);
                setShowSearchBar(false);
                setSearchQuery("");
            }
            // Close extra navbar if clicked outside
            const extraNavbar = document.querySelector('.extra-navbar');
            const aadviIcon = document.querySelector('.aadvi-icon');
            if (
                // Close extra navbar if clicked outside
                isExtraNavbarOpen &&
                extraNavbar &&
                !extraNavbar.contains(event.target) &&    // click outside extra navbar
                aadviIcon &&
                !aadviIcon.contains(event.target)   // click outside icon
            ) {
                setIsExtraNavbarOpen(false);
            }
        };
        // Add mousedown event listener
        document.addEventListener("mousedown", handleClickOutside);
        // Cleanup event listener
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isExtraNavbarOpen]);
    // Modal handlers
    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedProduct(null);
        setShowSearchResults(false);
        setShowSearchBar(false);
        setSearchQuery("");
    };
    // Toggle search bar visibility
    const toggleSearchBar = () => {
        setShowSearchBar(!showSearchBar);
        // If hiding the search bar, reset search state
        if (showSearchBar) {
            setSearchQuery("");
            setShowSearchResults(false);
            // Notify other components that search is closed
            window.dispatchEvent(new CustomEvent('bottomSearchOpen', { detail: { open: false } }));
        } else {
            window.dispatchEvent(new CustomEvent('bottomSearchOpen', { detail: { open: true } }));
        }
    };

    useEffect(() => {
        if (showSearchBar) {
            // Focus the search input when the search bar is shown
            const input = document.querySelector('.bottom-search-input');
            if (input) input.focus();
        }
    }, [showSearchBar]);

    return (
        <>
                        {isExtraNavbarOpen && (
                                <div className="extra-navbar" role="menu" aria-label="Quick links">
                                        <div
                                            className="extra-nav-item"
                                            role="menuitem"
                                            onClick={() => { navigate("/about"); setIsExtraNavbarOpen(false); }}
                                        >
                                            <span className="extra-nav-icon" aria-hidden>ℹ️</span>
                                            <span className="extra-nav-text">About</span>
                                        </div>
                                        <div
                                            className="extra-nav-item"
                                            role="menuitem"
                                            onClick={() => { navigate("/contact"); setIsExtraNavbarOpen(false); }}
                                        >
                                            <span className="extra-nav-icon" aria-hidden>✉️</span>
                                            <span className="extra-nav-text">Contact</span>
                                        </div>
                                </div>
                        )}
            <img
                src={AadviIcon}
                alt="Aadvi Icon"
                className={`aadvi-icon ${isIconSpinning ? (isExtraNavbarOpen ? 'spin-clockwise' : 'spin-counterclockwise') : ''}`}
                onClick={toggleExtraNavbar}
                style={{borderRadius:'50%', border:'2px solid #82278A', }}
            />
            <div className="bottom-navbar">
                <div className="bottom-nav-item" onClick={() => navigate("/")}><FaHome /><span>Home</span></div>
                <div className="bottom-nav-item" onClick={() => navigate("/products")}><FaShoppingBag /><span>Shop</span></div>
                <div className="bottom-nav-item" onClick={toggleSearchBar}><FaSearch /><span>Search</span></div>
                <div className="bottom-nav-item" onClick={() => navigate("/wishlist")}>
                    <FaHeart />
                    {wishlist.itemCount > 0 && <span className="badge" style={{marginLeft:'18px', borderRadius:'50%', fontWeight:'normal', display:'flex', justifyContent:'center'}}>{wishlist.itemCount}</span>}
                    <span>Wishlist</span>
                </div>
                <div className="bottom-nav-item" onClick={() => navigate("/cart")}>
                    <FaShoppingCart />
                    {cart.itemCount > 0 && <span className="badge" style={{marginLeft:'18px', borderRadius:'50%', fontWeight:'normal', display:'flex', justifyContent:'center'}}>{cart.itemCount}</span>}
                    <span>Cart</span>
                </div>
            </div>
            {showSearchBar && (
                <div className="bottom-search-container mt-1">
                    <input
                        className="bottom-search-input" type="search"
                        placeholder="Search products..." value={searchQuery}
                        onChange={handleSearch}
                    />
                    {(showSearchResults && searchQuery.trim()) && (
                        <div ref={searchRef} className="bottom-search-results">
                            {searchResults.length > 0 ? (
                                searchResults.map((result) => (
                                    <div
                                        key={result._id}
                                        className="search-result-item"
                                        onClick={() => {
                                            setSelectedProduct(result);
                                            setIsModalOpen(true);
                                        }}
                                    >
                                        {result.name}
                                    </div>
                                ))
                            ) : shouldShowNoMatches ? (
                                <div className="no-results-message">No Matches Found</div>
                            ) : null}
                        </div>
                    )}
                </div>
            )}
            <ProductModal post={selectedProduct} isOpen={isModalOpen} onClose={closeModal} />
        </>
    );
};

export default BottomNavbar;